<template lang="pug">
div(v-if="someVideosHaveThumbnail")
  h3(v-if="title") {{__(title, 'auto')}}
  Component(:is="layout === 'carousel' ? 'Carousel' : 'div'" :class="theme")
    .video(
      v-for="video in displayedVideos"
      :key="video.name"
      @click="$props.onSelect ? $props.onSelect(video) : $router.push(video.link)"
      :class="[layout, video.isActive && 'active']"
    )
  
      DynamicMedia.thumb(:config="video.thumbnail")
        .badge.live(v-if="video.live") {{__('Live', eventNamespace)}}
        .badge.upcoming(v-else-if="video.upcoming") {{__('Upcoming', eventNamespace)}}
      .description
        .name {{__(video.name, 'auto')}}
        .mobile-flex
          .event-name {{video.eventName}}
          .tags
            .paid(v-if="requirePremium && video.paid") {{__('Paid Content', eventNamespace)}}
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { eventNamespace } from '../../js/constants'

interface Video {
  name: string
  link: string
  thumbnail?: { src?: string, [key: string]: any }
  eventName?: string
  live?: boolean
  upcoming?: boolean
  paid?: boolean
  isActive?: boolean
  [key: string]: any
}

const props = withDefaults(defineProps<{
  videos?: Video[]
  layout?: 'carousel' | 'vertical'
  theme?: string
  requirePremium?: boolean
  title?: string
  // Declared as a prop so a parent's `@select` replaces the default router navigation
  onSelect?: (video: Video) => void
}>(), {
  videos         : () => [],
  layout         : 'carousel',
  requirePremium : true,
})

const displayedVideos = computed(() => props.videos.filter(video => video?.thumbnail?.src))
const someVideosHaveThumbnail = computed(() => displayedVideos.value.length > 0)
</script>

<style lang="scss" scoped>

.video {
  cursor: pointer;
  overflow: hidden;
  position: relative;
}

.thumb {
  position: relative;
}

.badge {
  text-transform: uppercase;
  font-size: 10px;
  font-weight: bold;
  position: absolute;
  bottom: $space-s;
  right: $space-s;
  padding: $space-xxxs $space-xxs;
  z-index: 1;
  color: white;
}

.live {
  background: red;
}

.upcoming {
  background: rgba($color-black, 0.5);
}

.paid {
  background: rgba(91, 189, 21, 0.2);;
  color: $color-green-dark;
  display: inline-block;
  padding: $space-xxxs $space-xxs;
  font-size: 10px;
  font-weight: bold;
}

.mobile-flex {
  @include mobile() {
    display: flex;
    align-items: center;
  }

  .tags {
    @include mobile() {
      display: flex;
      align-items: center;

      div {
        margin-left: $space-xs;
      }
    }
  }
}

.name {
  font-size: $size-xs;
  font-weight: $weight-bold;
  margin-bottom: $space-xxs;
}
.event-name {
  margin: 0 0 $space-xxs;
  font-size: $space-xs;
}

.video.carousel {
  margin-right: $space-s;
  width: 312px;

  &:last-child {
    margin-right: 0;
  }

  .thumb {
    height: 175px;
    width: 312px;
    margin-bottom: $space-xs;
  }
}

.video.vertical {
  width: 100%;
  display: flex;
  padding: $space-xs;

  @include not-desktop(){
    padding: 0;
    margin-bottom: $space-s;
    align-items: center;

    .thumb {
      flex-basis: 200px;
      flex-grow: 0;
      flex-shrink: 0;
    }

    .event-name {
      margin-bottom: 0;
    }

    .description {
      padding: $space-xs;
    }
  }

  @include all-desktop(){

    &.active {
      background-color: rgba(229, 246, 254, 0.75)
    }

    .thumb {
      flex-basis: 176px;
      height: 96px;
      margin-right: $space-s;
      flex-grow: 0;
      flex-shrink: 0;
    }

    .badge {
      bottom: $space-xxxs;
      right: $space-xxxs;
    }
  }
}

// Theme
.premium-promo {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .tags .paid {
    display: none;
  }

  .video {
    width: 24%;
  }

  @include mobile(){
    flex-direction: column;
    .video {
      width: 100%;
      margin-bottom: $space-s;
    }
  }

  .video .description {
    .event-name {
      display: none;
    }
    .name {
      font-size: $size-l;
      margin: $space-s 0;
      text-transform: uppercase;
    }
    .name {
      font-family: $font-industry;
    }
    .event-name {
      font-weight: $weight-normal;
    }
  }
}
</style>
